<script setup lang="ts">
/**
 * The bell and its panel. The bell carries one dot when something arrived since the last
 * read — and so does the panel's header, in words. There is no number anywhere: the feed is
 * read a page at a time and the server publishes no unread total, so any figure would be a
 * count of the rows that happen to be loaded rather than of what is unread (DR-6). The panel
 * is the product's menu surface (DropdownMenu's shell) with two groups, today and earlier: one
 * line per notification, the state word where the notification has one, and the
 * destination it opens. "Mark all read" moves the read position to the newest row.
 */
import { computed } from 'vue'
import { DropdownMenuContent, DropdownMenuPortal, DropdownMenuRoot, DropdownMenuTrigger } from 'reka-ui'
import Icon from '~/components/ui/Icon.vue'
import StatusCell from '~/components/ui/StatusCell.vue'
import ActionLink from '~/components/ui/ActionLink.vue'
import type { IconName } from '~/utils/icons'

export interface NotificationRow {
  id: string
  label: string
  facts: string
  status?: string
  destination: string
  to: string
  icon: IconName
  unread?: boolean
}
const props = withDefaults(defineProps<{
  today?: NotificationRow[]
  earlier?: NotificationRow[]
  /** Whether anything has arrived since the read position — a state, never a count. */
  unread?: boolean
  busy?: boolean
  canLoadMore?: boolean
  /** What an empty panel says. A read that failed says so instead of "Nothing yet." */
  emptyText?: string
}>(), { today: () => [], earlier: () => [], unread: false, busy: false, canLoadMore: false, emptyText: 'Nothing yet.' })
const emit = defineEmits<{ open: [row: NotificationRow]; markAll: []; loadMore: [] }>()
const open = defineModel<boolean>('open', { default: false })
const total = computed(() => props.today.length + props.earlier.length)
const groups = computed(() => [
  { id: 'today', label: 'Today', rows: props.today },
  { id: 'earlier', label: 'Earlier', rows: props.earlier },
].filter((group) => group.rows.length))
</script>

<template>
  <DropdownMenuRoot v-model:open="open" :modal="false">
    <DropdownMenuTrigger as-child>
      <button
type="button" class="relative grid size-7 place-items-center rounded-[var(--radius-control)] text-muted-foreground hover:bg-muted data-[state=open]:bg-muted"
        :aria-label="unread ? 'Notifications · unread' : 'Notifications'">
        <Icon name="bell" :size="15" />
        <span v-if="unread" class="absolute right-1 top-1 size-[var(--measure-7px)] rounded-full border border-sidebar bg-primary shadow-glow-primary" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuPortal>
      <DropdownMenuContent
        aria-label="Notifications" side="bottom" align="end" :side-offset="4" :collision-padding="8"
        class="z-50 flex w-[var(--menu-width-panel)] flex-col overflow-hidden rounded-lg border border-border bg-popover text-foreground shadow-[var(--shadow-menu)] outline-none">
        <div class="flex h-[var(--card-header-height)] shrink-0 items-center gap-2 border-b border-border px-3">
          <span class="text-xs font-semibold">Notifications</span>
          <span class="num text-[length:var(--type-meta)] text-muted-foreground">{{ unread ? 'unread' : 'all read' }}</span>
          <span class="flex-1" />
          <ActionLink v-if="unread" label="Mark all read" :disabled="busy" @click="emit('markAll')"><template #icon><Icon name="check" /></template></ActionLink>
        </div>

        <div class="flex max-h-[var(--measure-400px)] flex-col overflow-y-auto">
          <template v-for="group in groups" :key="group.id">
            <div class="flex h-[var(--table-head-height)] shrink-0 items-center bg-secondary/40 px-3 text-[length:var(--type-meta)] font-semibold text-muted-foreground">{{ group.label }}</div>
            <button
v-for="row in group.rows" :key="row.id" type="button"
              class="flex cursor-pointer items-start gap-2.5 border-b border-border/50 px-3 py-2 text-left last:border-b-0 hover:bg-sidebar-accent/60"
              @click="emit('open', row)">
              <Icon :name="row.icon" :size="13" class="mt-0.5 shrink-0 text-muted-foreground" />
              <span class="flex min-w-0 flex-1 flex-col gap-0.5">
                <span class="truncate text-xs" :class="row.unread ? 'font-medium' : 'text-muted-foreground'">{{ row.label }}</span>
                <span class="flex min-w-0 items-center gap-2">
                  <StatusCell v-if="row.status" :status="row.status" />
                  <span class="num truncate text-[length:var(--type-fine)] text-muted-foreground">{{ row.facts }}</span>
                </span>
              </span>
              <span v-if="row.unread" class="mt-1 size-[var(--measure-7px)] shrink-0 rounded-full bg-primary shadow-glow-primary" />
            </button>
          </template>
          <p v-if="!total" class="m-0 px-3 py-6 text-center text-xs text-muted-foreground">{{ emptyText }}</p>
        </div>

        <div class="flex h-[var(--list-footer-height)] shrink-0 items-center gap-2 border-t border-border px-3">
          <span class="num text-[length:var(--type-fine)] text-muted-foreground">{{ total }} shown</span>
          <span class="flex-1" />
          <ActionLink v-if="canLoadMore" label="Load more" :disabled="busy" @click="emit('loadMore')"><template #icon><Icon name="arrow" /></template></ActionLink>
        </div>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>